import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { authApi } from "@/lib/api/auth";
import { ApiRequestError } from "@/lib/api/client";
import { notify } from "@/lib/notify";
import { Button } from "@/components/ui/Button";
import { Input, PasswordInput } from "@/components/ui/Input";
import { ErrorMessage } from "@/components/ui/ErrorMessage";

const RESEND_COOLDOWN_SECONDS = 60;

type Step =
  | { name: "email" }
  | { name: "otp"; email: string }
  | { name: "reset"; ticket: string }
  | { name: "done" };

/**
 * Public "forgot password" flow: email -> emailed 6-digit code -> new
 * password -> sign in. Unauthenticated by design, same reasoning as
 * PlatformAdminActivationPage - the caller has no session yet. Each step is
 * its own small component so a step never needs to know the one before it
 * beyond the value it explicitly hands forward (email, then the reset
 * ticket VerifyResetOTP mints).
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ name: "email" });

  // Belt-and-suspenders for someone who reaches this page directly (typed
  // URL, bookmark) rather than via LoginPage's link, which already hides
  // itself when this tenant has password_reset turned off. Only an
  // explicit FEATURE_DISABLED flips this - a slow or offline check leaves
  // the wizard rendering normally, since the endpoints themselves enforce
  // the toggle regardless of what this page shows.
  const availability = useQuery({
    queryKey: ["auth", "password-reset-availability"],
    queryFn: () => authApi.passwordResetAvailability(),
    retry: false,
  });
  const disabled =
    availability.error instanceof ApiRequestError && availability.error.appError.code === "FEATURE_DISABLED";

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-brand px-4">
      <div className="w-full max-w-sm">
        {step.name !== "done" && !disabled && (
          <div className="mb-8 text-center">
            <h1 className="text-xl font-semibold text-white">Reset your password</h1>
            <p className="mt-1 text-sm text-primary-200">
              {step.name === "email" && "Enter the email you sign in with."}
              {step.name === "otp" && "Enter the 6-digit code we emailed you."}
              {step.name === "reset" && "Choose a new password."}
            </p>
          </div>
        )}

        <div className="rounded-2xl bg-white p-6 shadow-2xl">
          {disabled && (
            <div className="text-center">
              <h1 className="text-lg font-semibold text-slate-900">Not available</h1>
              <p className="mt-2 text-sm text-slate-500">
                Self-service password reset is not enabled for your organisation. Contact your workspace
                administrator to have your password reset.
              </p>
              <Button className="mt-4" fullWidth onClick={() => navigate("/login")}>
                Back to sign in
              </Button>
            </div>
          )}
          {!disabled && step.name === "email" && <EmailStep onSent={(email) => setStep({ name: "otp", email })} />}
          {!disabled && step.name === "otp" && (
            <OtpStep
              email={step.email}
              onVerified={(ticket) => setStep({ name: "reset", ticket })}
              onChangeEmail={() => setStep({ name: "email" })}
            />
          )}
          {!disabled && step.name === "reset" && (
            <ResetStep ticket={step.ticket} onDone={() => setStep({ name: "done" })} />
          )}
          {!disabled && step.name === "done" && (
            <div className="text-center">
              <h1 className="text-lg font-semibold text-slate-900">Password reset</h1>
              <p className="mt-2 text-sm text-slate-500">
                Your password has been changed. Sign in with your new password.
              </p>
              <Button className="mt-4" fullWidth onClick={() => navigate("/login")}>
                Go to sign in
              </Button>
            </div>
          )}
        </div>

        {step.name !== "done" && !disabled && (
          <p className="mt-6 text-center text-xs text-primary-300">
            Remembered it?{" "}
            <Link to="/login" className="underline">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

function EmailStep({ onSent }: { onSent: (email: string) => void }) {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<{ email: string }>();

  const mutation = useMutation({
    // An address that is not a login email comes back as an error and is
    // shown by the inline ErrorMessage below (no code is sent), so the
    // global toast is skipped to avoid saying it twice.
    meta: { silent: true },
    mutationFn: (values: { email: string }) => authApi.forgotPassword(values.email),
    onSuccess: () => onSent(getValues("email")),
  });

  return (
    <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="flex flex-col gap-4">
      <Input
        label="Email"
        type="email"
        autoComplete="username"
        placeholder="you@company.com"
        error={errors.email?.message}
        {...register("email", { required: "Email is required" })}
      />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button type="submit" loading={mutation.isPending} fullWidth className="mt-1">
        Send reset code
      </Button>
    </form>
  );
}

function OtpStep({
  email,
  onVerified,
  onChangeEmail,
}: {
  email: string;
  onVerified: (ticket: string) => void;
  onChangeEmail: () => void;
}) {
  const [cooldown, setCooldown] = useState(0);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ code: string }>();

  const verify = useMutation({
    meta: { silent: true },
    mutationFn: (values: { code: string }) => authApi.verifyResetOtp(email, values.code),
    onSuccess: (result) => onVerified(result.reset_ticket),
  });

  const resend = useMutation({
    meta: { silent: true },
    mutationFn: () => authApi.forgotPassword(email),
    onSuccess: () => {
      notify.success("Code resent - check your inbox.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      const timer = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) {
            clearInterval(timer);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    },
  });

  return (
    <form onSubmit={handleSubmit((values) => verify.mutate(values))} className="flex flex-col gap-4">
      <p className="text-sm text-slate-500">
        We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>. It expires in 10
        minutes.
      </p>
      <Input
        label="Verification code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="123456"
        error={errors.code?.message}
        {...register("code", {
          required: "Enter the 6-digit code",
          pattern: { value: /^\d{6}$/, message: "Enter all 6 digits" },
        })}
      />
      {verify.isError && <ErrorMessage error={verify.error} />}
      <Button type="submit" loading={verify.isPending} fullWidth className="mt-1">
        Verify code
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onChangeEmail} className="text-slate-500 underline">
          Use a different email
        </button>
        <button
          type="button"
          disabled={cooldown > 0 || resend.isPending}
          onClick={() => resend.mutate()}
          className="text-primary-700 underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
        </button>
      </div>
    </form>
  );
}

function ResetStep({ ticket, onDone }: { ticket: string; onDone: () => void }) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<{ password: string; confirm: string }>();

  const mutation = useMutation({
    meta: { silent: true },
    mutationFn: (values: { password: string; confirm: string }) =>
      authApi.resetPasswordWithOtp(ticket, values.password, values.confirm),
    onSuccess: onDone,
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        for (const field of error.appError.fields ?? []) {
          if (field.field === "password") setError("password", { message: field.message });
          if (field.field === "confirm_password") setError("confirm", { message: field.message });
        }
      }
    },
  });

  return (
    <form
      onSubmit={handleSubmit((values) => {
        if (values.password !== values.confirm) {
          setError("confirm", { message: "Passwords do not match" });
          return;
        }
        mutation.mutate(values);
      })}
      className="flex flex-col gap-4"
    >
      <PasswordInput
        label="New password"
        autoComplete="new-password"
        hint="At least 10 characters, with uppercase, lowercase, a number and a special character."
        error={errors.password?.message}
        {...register("password", { required: true, minLength: 10 })}
      />
      <PasswordInput
        label="Confirm new password"
        autoComplete="new-password"
        error={errors.confirm?.message}
        {...register("confirm", { required: true })}
      />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button type="submit" loading={mutation.isPending} fullWidth className="mt-1">
        Reset password
      </Button>
    </form>
  );
}
